import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Row, Col, Upload, message, Divider, Card, InputNumber, Space } from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { 
  getProductVariationTypes, 
  createProductVariationType, 
  createProductVariation,
  updateProductVariation,
  uploadVariationImage,
  deleteVariationImage,
  getProductVariationById,
  getProductById,
  getProductVariationsByProductId,
  createVariationPricing,
  updateVariationPricing,
  getProductPricingByProductVariation,
  deleteVariationPricing,
  getImagesByVariationId
} from '../../../core/api/products';
import { getAllStatus } from '../../../core/api/settings';

const { Option } = Select;
const { TextArea } = Input;

const VariationForm = ({ productId, variation, onSuccess, onCancel, uniqueId }) => {
  const [form] = Form.useForm();
  const [variationTypes, setVariationTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [variationData, setVariationData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [variationCount, setVariationCount] = useState(0);
  const [pricingError, setPricingError] = useState('');
  const [pricingForm] = Form.useForm();
  const [pricings, setPricings] = useState([{ sku: '', original_price: '' }]);
  const [existingPricings, setExistingPricings] = useState([]);
  // Generate unique ID for file input
  const imageInputId = `variationImageUpload_${uniqueId}_${variation?.id || 'new'}`;

  useEffect(() => {
    fetchDropdownData();
    
    if (productId) {
      fetchProductDetails();
    }
    
    if (variation?.id) {
      fetchVariationDetails(variation.id);
      // Fetch variation images if editing an existing variation
      fetchVariationImages(variation.id);
    } else {
      // For new variations, get the count of existing variations
      fetchVariationCount();
    }
  }, [variation, productId]);
  
  // Update variation code and description in real time when form values change
  useEffect(() => {
    // Only auto-generate for new variations, not when editing
    if (!variation?.id && productData && productData.product_code) {
      updateAutoGeneratedFields();
    }
  }, [productData]);
  

  
  // Function to update the auto-generated fields
  const updateAutoGeneratedFields = () => {
    if (!productData) return;
    
    const currentValues = form.getFieldsValue();
    const type = currentValues.product_variation_type;
    const name = currentValues.product_variation;
    
    // Generate variation code
    const baseCode = productData.product_code || '';
    const nextNumber = variationCount + 1;
    const generatedCode = `${baseCode}${nextNumber}`;
    
    // Generate description
    let generatedDesc = '';
    if (type && name) {
      const variationType = variationTypes.find(t => t.id === type);
      const typeLabel = variationType ? variationType.name : '';
      const productName = productData.name || '';
      generatedDesc = `${typeLabel}-${productName} ${name}`;
    }
    
    // Update form fields
    form.setFieldsValue({
      product_variation_code: generatedCode,
      product_variation_desc: generatedDesc
    });
  };

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const response = await getProductById(productId);
      if (response && response.data) {
        setProductData(response.data);
      }
    } catch (error) {
      message.error('Failed to fetch product details');
      console.error('Error fetching product details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVariationCount = async () => {
    try {
      const response = await getProductVariationsByProductId(productId);
      if (response && response.data) {
        setVariationCount(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching variation count:', error);
    }
  };

  const fetchVariationDetails = async (variationId) => {
    try {
      setLoading(true);
      const response = await getProductVariationById(variationId);
      if (response && response.data) {
        setVariationData(response.data);

        console.log('variationData', response.data);
        console.log('Setting form values with data:', response.data);
        
        // Initialize form with variation data
        form.setFieldsValue({
          product_variation_type: response.data.product_variation_type,
          product_variation: response.data.product_variation,
          logistic_codification: response.data.logistic_codification,
          program_time: response.data.program_time,
          posology: response.data.posology,
          product_variation_code: response.data.product_variation_code,
          product_variation_desc: response.data.product_variation_desc,
          status: response.data.status,
          status_description: response.data.status_description,
          variation_code: response.data.variation_code,
          formula_variation_from_lab: response.data.formula_variation_from_lab,
          supplement_facts: response.data.supplement_facts,
          upc_code: response.data.upc_code,
          manufactured_description: response.data.manufactured_description,
          front_claims: response.data.front_claims,
          fda_statements: response.data.fda_statements,
          labels_approved_by_lab: response.data.labels_approved_by_lab,
        });
        
        // Load images if any
        if (response.data.images && response.data.images.length > 0) {
          const imageList = response.data.images.map(img => ({
            uid: img.id,
            name: img.image.split('/').pop(),
            status: 'done',
            url: img.image,
            imageId: img.id
          }));
          setFileList(imageList);
          
          // Store preview URLs for later use
          const urls = response.data.images.map(img => img.image);
          setPreviewUrls(urls);
        }
        
        // Fetch pricing data for this variation
        fetchPricingData(variationId);
      }
    } catch (error) {
      message.error('Failed to fetch variation details');
      console.error('Error fetching variation details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingData = async (variationId) => {
    try {
      const response = await getProductPricingByProductVariation(variationId);
      if (response && response.data) {
        const pricingData = response.data.map(pricing => ({
          id: pricing.id,
          sku: pricing.sku,
          original_price: pricing.original_price
        }));
        
        if (pricingData.length > 0) {
          setPricings(pricingData);
          setExistingPricings(pricingData);
        }
      }
    } catch (error) {
      message.error('Failed to fetch pricing details');
      console.error('Error fetching pricing details:', error);
    }
  };

  const fetchDropdownData = async () => {
    try {
      setLoading(true);
      const [typesResponse, statusesResponse] = await Promise.all([
        getProductVariationTypes(),
        getAllStatus()
      ]);
      
      if (typesResponse.data) {
        setVariationTypes(typesResponse.data);
      }
      
      if (statusesResponse.data) {
        setStatuses(statusesResponse.data);
      }
    } catch (error) {
      message.error('Failed to load dropdown data');
      console.error('Error fetching dropdown data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVariationType = async () => {
    if (!newTypeName.trim()) {
      message.error('Please enter a variation type name');
      return;
    }
    
    try {
      setAddingType(true);
      const response = await createProductVariationType({ name: newTypeName.trim() });
      if (response.data) {
        setVariationTypes([...variationTypes, response.data]);
        form.setFieldsValue({ product_variation_type: response.data.id });
        setNewTypeName('');
        message.success('Variation type added successfully');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to add variation type');
      console.error('Error adding variation type:', error);
    } finally {
      setAddingType(false);
    }
  };

  const handleRemoveImage = async (file) => {
    // If the image has an ID, it's an existing image that we'll delete during form submission
    if (file.imageId && variation?.id) {
      // Mark for deletion but don't actually delete yet
      setImagesToDelete(prev => [...prev, file.imageId]);
      file.removed = true;
    }
    return true;
  };

  const fetchVariationImages = async (variationId) => {
    try {
      setLoading(true);
      const response = await getImagesByVariationId(variationId);
      if (response && response.data) {
        const images = response.data.map(img => ({
          uid: img.id,
          name: img.image.split('/').pop(),
          status: 'done',
          url: img.image,
          imageId: img.id
        }));
        setFileList(images);
      }
    } catch (error) {
      console.error('Error fetching variation images:', error);
      message.error('Failed to fetch variation images');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    // Check if form has errors
    try {
      form.validateFields();
      
      // Check if at least one pricing is provided
      if (pricings.length === 0) {
        setPricingError('At least one pricing is required');
        return false;
      }
      
      // Check if all pricing fields are filled
      const invalidPricing = pricings.some(pricing => !pricing.original_price);
      if (invalidPricing) {
        setPricingError('All pricing fields must be filled');
        return false;
      }
      
      setPricingError('');
      return true;
    } catch (errorInfo) {
      console.log('Form validation failed:', errorInfo);
      return false;
    }
  };

  const generateVariationCode = (values) => {
    if (!productData || !productData.product_code) return '';
    
    const baseCode = productData.product_code;
    const nextNumber = variation?.id ? variationData?.product_variation_code?.replace(baseCode, '') || '1' : (variationCount + 1);
    
    return `${baseCode}${nextNumber}`;
  };
  
  const generateVariationDescription = (values) => {
    if (!values.product_variation_type || !values.product_variation) return '';
    
    const variationType = variationTypes.find(type => type.id === values.product_variation_type);
    const typeLabel = variationType ? variationType.name : '';
    const productName = productData ? productData.name : '';
    
    return `${typeLabel}-${productName} ${values.product_variation}`;
  };

  const handleSubmit = async () => {
    // Get form values manually since we're using onClick instead of onFinish
    const values = form.getFieldsValue();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      
      if (!values.product_variation_code) {
        values.product_variation_code = generateVariationCode(values);
      }
      
      if (!values.product_variation_desc) {
        values.product_variation_desc = generateVariationDescription(values);
      }
      
      // Create a plain object first (not FormData)
      const dataToSend = {
        ...values,
        product_id: productId,
        product: productId  // Add the product field as required by the backend
      };
      
      let response;
      let variationId;
      
      if (variation?.id) {
        // Update existing variation
        response = await updateProductVariation(variation.id, dataToSend);
        variationId = variation.id;
        
        // Delete images marked for deletion
        if (imagesToDelete.length > 0) {
          await Promise.all(imagesToDelete.map(imageId => 
            deleteVariationImage(imageId)
          ));
        }
        
        // Handle image uploads if any - only for new images with originFileObj
        const newImages = fileList.filter(file => file.originFileObj);
        if (newImages.length > 0) {
          await Promise.all(newImages.map(async file => {
            await uploadVariationImage(variationId, file.originFileObj);
          }));
        }
        
        // Update or create pricing records
        await handlePricingSubmit(variationId);
        
        message.success('Variation updated successfully');
      } else {
        // Create new variation
        response = await createProductVariation(dataToSend);
        variationId = response.data.id;
        
        // Handle image uploads if any - only for new images with originFileObj
        const newImages = fileList.filter(file => file.originFileObj);
        if (newImages.length > 0 && variationId) {
          await Promise.all(newImages.map(async file => {
            await uploadVariationImage(variationId, file.originFileObj);
          }));
        }
        
        // Create pricing records
        await handlePricingSubmit(variationId);
        
        message.success('Variation created successfully');
      }
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save variation');
      console.error('Error saving variation:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handlePricingSubmit = async (variationId) => {
    try {
      // Process each pricing item
      const pricingPromises = pricings.map(async (pricing) => {
        const pricingData = {
          sku: pricing.sku,
          original_price: pricing.original_price,
          product_variation: variationId
        };
        
        // If pricing has an ID, update it, otherwise create a new one
        if (pricing.id) {
          return await updateVariationPricing(pricing.id, pricingData);
        } else {
          return await createVariationPricing(pricingData);
        }
      });
      
      // Delete any removed pricing items
      if (existingPricings.length > 0) {
        const currentPricingIds = pricings
          .filter(p => p.id) // Only consider pricings with IDs
          .map(p => p.id);
        
        const pricingsToDelete = existingPricings
          .filter(p => !currentPricingIds.includes(p.id))
          .map(p => p.id);
        
        if (pricingsToDelete.length > 0) {
          const deletePricingPromises = pricingsToDelete.map(id => 
            deleteVariationPricing(id)
          );
          await Promise.all(deletePricingPromises);
        }
      }
      
      // Wait for all pricing operations to complete
      await Promise.all(pricingPromises);
      
      return true;
    } catch (error) {
      message.error('Failed to save pricing information');
      console.error('Error saving pricing:', error);
      return false;
    }
  };

  const handleAddPricing = () => {
    const newIndex = pricings.length;
    setPricings([...pricings, { sku: generateSku(newIndex), original_price: '' }]);
  };

  const handleRemovePricing = (index) => {
    // Don't allow removing the last pricing item
    if (pricings.length === 1) {
      message.warning('At least one pricing is required');
      return;
    }
    
    const newPricings = [...pricings];
    newPricings.splice(index, 1);
    setPricings(newPricings);
  };

  const handlePricingChange = (index, field, value) => {
    const newPricings = [...pricings];
    newPricings[index] = { ...newPricings[index], [field]: value };
    setPricings(newPricings);
  };

  const generateSku = (index) => {
    const variationCode = form.getFieldValue('product_variation_code') || '';
    return variationCode ? `${variationCode}-${index + 1}` : '';
  };

  // Update SKUs when variation code changes
  useEffect(() => {
    if (!variation?.id) { // Only auto-generate for new variations
      const variationCode = form.getFieldValue('product_variation_code');
      if (variationCode) {
        const newPricings = pricings.map((pricing, index) => ({
          ...pricing,
          sku: generateSku(index)
        }));
        setPricings(newPricings);
      }
    }
  }, [form.getFieldValue('product_variation_code')]);

  const handleImageChange = ({ fileList: newFileList }) => {
    // Filter out files that are marked for removal
    const filteredFileList = newFileList.filter(file => !file.removed);
    setFileList(filteredFileList);
    
    // Generate preview URLs for new files
    const newPreviewUrls = filteredFileList.map(file => {
      if (file.url) {
        return file.url;
      }
      return file.originFileObj ? URL.createObjectURL(file.originFileObj) : '';
    });
    
    setPreviewUrls(newPreviewUrls);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      // onFinish={handleSubmit}
      initialValues={{
        product_variation: '',
        program_time: '',
        posology: '',
        logistic_codification: '',
        product_variation_desc: '',
      }}
    >
      <Card title="Product Variation" className="mb-4" size="small">
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="product_variation_type"
              label="Variation Type"
              rules={[{ required: true, message: 'Please select variation type' }]}
            >
              <Select
                placeholder="Select variation type"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onChange={(value) => {
                  // Update auto-generated fields when variation type changes
                  updateAutoGeneratedFields();
                }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {addingType ? (
                      <div style={{ padding: '8px' }}>
                        <Input
                          placeholder="Type name"
                          value={newTypeName}
                          onChange={(e) => setNewTypeName(e.target.value)}
                          style={{ marginBottom: '8px' }}
                        />
                        <Button
                          type="primary"
                          onClick={handleAddVariationType}
                          loading={false}
                          block
                        >
                          Add Type
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={() => setAddingType(true)}
                        style={{ width: '100%', textAlign: 'left' }}
                      >
                        Add New Type
                      </Button>
                    )}
                  </>
                )}
              >
                {variationTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="product_variation"
              label="Variation Name"
              rules={[{ required: true, message: 'Please enter variation name' }]}
            >
              <Input 
                placeholder="Enter variation name" 
                onChange={(e) => {
                  // Update auto-generated fields when variation name changes
                  updateAutoGeneratedFields();
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="product_variation_code"
              label="Variation Code"
              tooltip="Auto-generated based on product code and variation number"
            >
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="product_variation_desc"
              label="Description"
              tooltip="Auto-generated as variation type-productname concatenated with variation name"
            >
              <Input disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="program_time"
              label="Program Time"
            >
              <Input placeholder="Enter program time" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="posology"
              label="Posology"
            >
              <Input placeholder="Enter posology" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="logistic_codification"
              label="Logistic Codification"
            >
              <Input placeholder="Enter logistic codification" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select 
                placeholder="Select status"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {statuses.map((status) => (
                  <Option key={status.id} value={status.id}>
                    {status.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="status_description"
              label="Status Description"
            >
              <Input placeholder="Enter status description" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card 
        title="Pricing Information" 
        className="mb-4" 
        size="small"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddPricing}
          >
            Add Pricing
          </Button>
        }
      >
        {pricings.map((pricing, index) => (
          <div key={index} style={{ marginBottom: index < pricings.length - 1 ? 16 : 0 }}>
            {index > 0 && <Divider style={{ margin: '12px 0' }} />}
            <Row gutter={16} align="middle">
              <Col xs={24} md={10}>
                <Form.Item
                  label="SKU"
                  required
                  validateStatus={pricingError.includes('SKU') ? 'error' : ''}
                  style={{ marginBottom: 8 }}
                >
                  <Input 
                    placeholder="Auto-generated SKU" 
                    value={pricing.sku}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={10}>
                <Form.Item
                  label="Price"
                  required
                  validateStatus={pricingError.includes('price') ? 'error' : ''}
                  style={{ marginBottom: 8 }}
                >
                  <InputNumber 
                    style={{ width: '100%' }} 
                    precision={2} 
                    min={0.01}
                    placeholder="0.00"
                    value={pricing.original_price}
                    onChange={(value) => handlePricingChange(index, 'original_price', value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={4} style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
                <Button 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={() => handleRemovePricing(index)}
                  disabled={pricings.length === 1}
                  style={{ marginBottom: 8 }}
                >
                  Remove
                </Button>
              </Col>
            </Row>
          </div>
        ))}
        {pricingError && (
          <div style={{ color: '#ff4d4f', marginTop: 8 }}>{pricingError}</div>
        )}
      </Card>
      
      <Card title="Additional Details" className="mb-4" size="small">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="formula_variation_from_lab"
              label="Formula Variation from Lab"
            >
              <Select 
                placeholder="Select status"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                <Option value="Approved">Approved</Option>
                <Option value="Requested">Requested</Option>
                <Option value="Pending">Pending</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="labels_approved_by_lab"
              label="Labels Approved by Lab"
            >
              <Select 
                placeholder="Select status"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                <Option value="Approved">Approved</Option>
                <Option value="Pending">Pending</Option>
                <Option value="Not Required">Not Required</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="supplement_facts"
              label="Supplement Facts"
            >
              <TextArea rows={4} placeholder="Enter supplement facts" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="upc_code"
              label="UPC Code"
            >
              <Input placeholder="Enter UPC code" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="manufactured_description"
              label="Manufactured Description"
            >
              <TextArea rows={4} placeholder="Enter manufactured description" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="front_claims"
              label="Front Claims"
            >
              <TextArea rows={4} placeholder="Enter front claims" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="fda_statements"
          label="FDA Statements"
        >
          <TextArea rows={4} placeholder="Enter FDA statements" />
        </Form.Item>
      </Card>

      <Card title="Images" className="mb-4" size="small">
        <Form.Item label="Variation Images">
          <Upload
            listType="picture-card"
            fileList={fileList}
            onChange={handleImageChange}
            onRemove={handleRemoveImage}
            beforeUpload={() => false} // Prevent auto upload
            id={imageInputId}
          >
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          </Upload>
        </Form.Item>
      </Card>
      <div className="d-flex justify-content-end mt-4">
        <Button 
          onClick={onCancel} 
          className="me-2"
        >
          Cancel
        </Button>
        <Button 
          type="primary" 
          onClick={handleSubmit}
          loading={submitting}
        >
          {variation?.id ? 'Update Variation' : 'Create Variation'}
        </Button>
      </div>
    </Form>
  );
};

export default VariationForm;
